import type { ToolResult } from "@/types/tools";

export type TimestampUnit = "seconds" | "milliseconds";
export type TimestampInputUnit = TimestampUnit | "auto";

export type TimestampDetails = {
  milliseconds: number;
  seconds: number;
  iso: string;
  utc: string;
};

export type ParsedTimestamp = TimestampDetails & {
  detectedUnit: TimestampUnit;
};

const maxDateMilliseconds = 8.64e15;
const localTimestampFormatOptions: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  second: "2-digit",
  timeZoneName: "short",
};

export function formatLocalTimestamp(milliseconds: number) {
  return new Date(milliseconds).toLocaleString(undefined, localTimestampFormatOptions);
}

function createTimestampDetails(milliseconds: number): ToolResult<TimestampDetails> {
  const roundedMilliseconds = Math.round(milliseconds);

  if (
    !Number.isFinite(roundedMilliseconds) ||
    Math.abs(roundedMilliseconds) > maxDateMilliseconds
  ) {
    return { ok: false, error: "Enter a timestamp within JavaScript's supported date range." };
  }

  const date = new Date(roundedMilliseconds);

  if (Number.isNaN(date.getTime())) {
    return { ok: false, error: "Enter a timestamp that can be converted to a valid date." };
  }

  return {
    ok: true,
    value: {
      milliseconds: date.getTime(),
      seconds: Math.floor(date.getTime() / 1000),
      iso: date.toISOString(),
      utc: date.toUTCString(),
    },
  };
}

function detectUnixUnit(value: number): TimestampUnit {
  return Math.abs(value) >= 100000000000 ? "milliseconds" : "seconds";
}

export function parseUnixTimestamp(
  input: string,
  unit: TimestampInputUnit = "auto",
): ToolResult<ParsedTimestamp> {
  const trimmed = input.trim();

  if (!trimmed) {
    return { ok: false, error: "Enter a Unix timestamp first." };
  }

  const numericValue = Number(trimmed);

  if (!Number.isFinite(numericValue)) {
    return { ok: false, error: "Enter a numeric Unix timestamp." };
  }

  const detectedUnit = unit === "auto" ? detectUnixUnit(numericValue) : unit;
  const milliseconds = detectedUnit === "seconds" ? numericValue * 1000 : numericValue;
  const result = createTimestampDetails(milliseconds);

  return result.ok
    ? { ok: true, value: { ...result.value, detectedUnit } }
    : result;
}

export function parseDateTime(input: string): ToolResult<TimestampDetails> {
  const trimmed = input.trim();

  if (!trimmed) {
    return { ok: false, error: "Enter a date or time first." };
  }

  return createTimestampDetails(new Date(trimmed).getTime());
}
